'use client';

import Image from 'next/image';
import { LoaderCircle, Search, ShieldCheck, Trash2, UserPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { PortalConfig } from '../lib/portal-config';
import {
  addPermissionAssignment,
  fetchPermissionAssignments,
  lookupPermissionUser,
  removePermissionAssignment,
  type PermissionAssignments,
  type PermissionUser,
} from '../lib/wiki-permissions';
import './portal-permission-manager.css';

type Props = { games: PortalConfig['games']; active: boolean };
type AssignRole = 'super_admin' | 'moderator';

const emptyAssignments: PermissionAssignments = {
  superAdmins: [],
  builtInSuperAdmins: [],
  moderators: [],
};

export default function PortalPermissionManager({ games, active }: Props) {
  const [assignments, setAssignments] = useState(emptyAssignments);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uid, setUID] = useState('');
  const [role, setRole] = useState<AssignRole>('moderator');
  const [gameKey, setGameKey] = useState(games[0]?.key || games[0]?.id || '');
  const [candidate, setCandidate] = useState<PermissionUser | null>(null);
  const [working, setWorking] = useState(false);
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setAssignments(await fetchPermissionAssignments());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '权限列表读取失败');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (active) void load();
  }, [active]);
  const close = () => {
    if (working) return;
    setDialogOpen(false);
    setUID('');
    setCandidate(null);
    setError('');
  };
  const lookup = async () => {
    const value = uid.trim();
    if (!/^[1-9]\d{0,19}$/.test(value)) return setError('请输入正确的用户 UID');
    setWorking(true);
    setError('');
    setCandidate(null);
    try {
      setCandidate(await lookupPermissionUser(value));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '用户资料查询失败');
    } finally {
      setWorking(false);
    }
  };
  const add = async () => {
    if (!candidate) return;
    if (role === 'moderator' && !gameKey) return setError('请选择版主所属游戏');
    setWorking(true);
    setError('');
    try {
      await addPermissionAssignment({ uid: candidate.uid, role, gameKey: role === 'moderator' ? gameKey : undefined });
      await load();
      setDialogOpen(false);
      setUID('');
      setCandidate(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '权限添加失败');
    } finally {
      setWorking(false);
    }
  };
  const remove = async (item: PermissionUser, itemRole: AssignRole) => {
    if (!window.confirm(`确定移除 ${item.username || `UID ${item.uid}`} 的${itemRole === 'super_admin' ? '超管' : '版主'}权限吗？`)) return;
    setWorking(true);
    setError('');
    try {
      await removePermissionAssignment({ uid: item.uid, role: itemRole, gameKey: item.gameKey });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '权限移除失败');
    } finally {
      setWorking(false);
    }
  };
  const gameName = (key?: string) => games.find((game) => game.key === key || game.id === key)?.name || key || '未知游戏';
  const superAdmins = [...assignments.builtInSuperAdmins, ...assignments.superAdmins];
  return (
    <div className="portal-permission-panel">
      <div className="portal-admin-drawer-actions">
        <div><strong>权限管理</strong><p>管理平台超管与各游戏 Wiki 版主。</p></div>
        <button type="button" onClick={() => setDialogOpen(true)}><UserPlus />添加权限</button>
      </div>
      {error && <p className="portal-permission-error" role="alert">{error}</p>}
      <div className="portal-permission-lists">
        <section>
          <header><span><ShieldCheck />超管</span><small>全平台最高权限</small></header>
          {superAdmins.map((item) => (
            <article key={`super-${item.uid}`}>
              <span className="portal-permission-avatar">{(item.username || item.uid).slice(0, 1)}{item.avatar && <Image src={item.avatar} width={38} height={38} unoptimized alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}</span>
              <div><strong>{item.username || '用户'}</strong><small>UID {item.uid}{item.builtIn ? ' · 系统预设' : ''}</small></div>
              {!item.builtIn && <button type="button" onClick={() => void remove(item, 'super_admin')} title="移除超管"><Trash2 /></button>}
            </article>
          ))}
          {!loading && !superAdmins.length && <p className="portal-permission-empty">暂无已添加超管</p>}
        </section>
        <section>
          <header><span><ShieldCheck />游戏版主</span><small>单个 Wiki 最高权限</small></header>
          {assignments.moderators.map((item) => (
            <article key={`moderator-${item.gameKey}-${item.uid}`}>
              <span className="portal-permission-avatar">{(item.username || item.uid).slice(0, 1)}{item.avatar && <Image src={item.avatar} width={38} height={38} unoptimized alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}</span>
              <div><strong>{item.username || '用户'}</strong><small>{gameName(item.gameKey)} · UID {item.uid}</small></div>
              <button type="button" onClick={() => void remove(item, 'moderator')} title="移除版主"><Trash2 /></button>
            </article>
          ))}
          {!loading && !assignments.moderators.length && <p className="portal-permission-empty">暂无游戏版主</p>}
        </section>
        {loading && <p className="portal-permission-loading"><LoaderCircle className="is-spinning" />正在读取权限…</p>}
      </div>
      {dialogOpen && (
        <div className="portal-permission-dialog-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
          <dialog open className="portal-permission-dialog" aria-labelledby="portal-permission-title">
            <header><div><small>ACCESS CONTROL</small><h2 id="portal-permission-title">添加权限</h2></div><button type="button" onClick={close}><X /></button></header>
            <section>
              <label>权限类型</label>
              <div className="portal-permission-role-options">
                <button className={role === 'moderator' ? 'is-active' : ''} type="button" onClick={() => setRole('moderator')}><strong>游戏版主</strong><small>管理一个游戏 Wiki，可添加普通管理员</small></button>
                <button className={role === 'super_admin' ? 'is-active' : ''} type="button" onClick={() => setRole('super_admin')}><strong>超管</strong><small>管理所有游戏 Wiki 与平台权限</small></button>
              </div>
              {role === 'moderator' && <label>所属游戏<select value={gameKey} onChange={(event) => setGameKey(event.target.value)}>{games.map((game) => <option value={game.key || game.id} key={game.key || game.id}>{game.name}</option>)}</select></label>}
              <label>用户 UID</label>
              <div className="portal-permission-search"><input value={uid} onChange={(event) => { setUID(event.target.value.replace(/\D/g, '')); setCandidate(null); }} placeholder="输入 GameKee UID" /><button type="button" disabled={working} onClick={() => void lookup()}><Search />查询</button></div>
              {candidate && <div className="portal-permission-candidate"><span className="portal-permission-avatar">{(candidate.username || candidate.uid).slice(0, 1)}{candidate.avatar && <Image src={candidate.avatar} width={44} height={44} unoptimized alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}</span><div><strong>{candidate.username || '用户'}</strong><small>UID {candidate.uid}</small></div></div>}
              {error && <p className="portal-permission-error" role="alert">{error}</p>}
            </section>
            <footer><button type="button" onClick={close}>取消</button><button className="primary" type="button" disabled={!candidate || working || (role === 'moderator' && !gameKey)} onClick={() => void add()}>{working ? '处理中…' : '确认添加'}</button></footer>
          </dialog>
        </div>
      )}
    </div>
  );
}
